import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/**
 * KMH (kredili mevduat / ek hesap): nakit avans limiti tanımlı banka hesabı, limitten açık nakit avans düşüldükten sonra
 * kalan kadar eksiye inebilir. Faiz gün sonu eksi bakiye üzerinden günlük işler; ay başında önceki ayın faizi + BSMV +
 * KKDF hesaptan düşülür (780.04).
 */
export const KMH_READ = 'finance:kmh:read'
export const KMH_ACCRUE = 'finance:kmh:accrue'

export type KMHLine = { from: string; to: string; days: number; used: string; amount: string }

export type KMHAccrual = {
  id: string
  periodFrom: string
  periodTo: string
  rate: string
  interest: string
  bsmv: string
  kkdf: string
  total: string
  lines: KMHLine[]
}

export type KMHAccount = {
  accountId: string
  name: string
  bankName: string
  limit: string
  cashAdvance: string
  kmhUsed: string
  available: string
  balance: string
  /** Son tahakkuktan bugüne işleyen (henüz hesaptan düşülmemiş) faiz. */
  running: KMHAccrual | null
  accruals: KMHAccrual[]
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

function mapAccrual(r: Row): KMHAccrual {
  return {
    id: s(r.id),
    periodFrom: s(r.period_from),
    periodTo: s(r.period_to),
    rate: s(r.rate),
    interest: s(r.interest),
    bsmv: s(r.bsmv),
    kkdf: s(r.kkdf),
    total: s(r.total),
    lines: (Array.isArray(r.lines) ? (r.lines as Row[]) : []).map((l) => ({
      from: s(l.from),
      to: s(l.to),
      days: Number(l.days) || 0,
      used: s(l.used),
      amount: s(l.amount),
    })),
  }
}

export const kmhKeys = { all: ['finance', 'kmh'] as const }

export const useKMH = (enabled = true) =>
  useQuery({
    queryKey: kmhKeys.all,
    enabled,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/kmh')).map(
        (r): KMHAccount => ({
          accountId: s(r.account_id),
          name: s(r.name),
          bankName: s(r.bank_name),
          limit: s(r.limit),
          cashAdvance: s(r.cash_advance),
          kmhUsed: s(r.kmh_used),
          available: s(r.available),
          balance: s(r.balance),
          running: r.running && typeof r.running === 'object' ? mapAccrual(r.running as Row) : null,
          accruals: (Array.isArray(r.accruals) ? (r.accruals as Row[]) : []).map(mapAccrual),
        }),
      ),
  })

/** Faizi verilen güne (boşsa dün) kadar elle tahakkuk ettirir; hesap bakiyeleri de değişir. */
export const useKMHAccrue = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, until }: { accountId: string; until: string }) =>
      api.post(`/api/v1/finance/kmh/${accountId}/accrue`, { until }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }),
  })
}
